import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  CachedPortfolioService,
  ENVIRONMENT,
  ModalService,
  PageSectionModule,
  PageSectionPortfolioModule,
  SharedService,
  UserService
} from '@cfp/core';
import { AddBuildingService } from '@cfp/domains/portfolio/add-building-service';
import { PortfolioHeader } from '@cfp/domains/portfolio/header';
import { BuildingSearchFiltersService, FilterBadge } from '@cfp/shared/building/filters';
import { PortfolioLayoutData } from '@cfp/shared/portfolio/layout';
import { RouteUtility } from '@cfp/shared/router';
import { LabelGroupService } from '@cfp/shared/ui/label-group';
import { LayoutContainer } from '@cfp/shared/ui/layout-container';
import { MeasurementService } from '@cfp/shared/ui/measurement';
import { NavService } from '@cfp/shared/ui/nav';
import { AlertModalResult, AlertService, ErrorService, Icon, Link } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import {
  BuildingSearchFacetsDTO,
  BuildingSearchFiltersDTO,
  FrontendPortfolioImportDTO,
  FrontendPortfolioImportService,
  LabelGroupDTO,
  PortfoliosService,
  SearchFiltersService
} from 'csr-asset-app';
import { LicenseDTO } from 'csr-login-app';
import { catchError, combineLatest, map, mergeMap, of, startWith, switchMap, tap } from 'rxjs';
import { BuildingImportService } from '../import-buildings';
import { mapToPortfolioDTO } from '../utils';

@Component({
  selector: 'cfp-portfolio-layout',
  templateUrl: './portfolio-layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LayoutContainer,
    PageSectionModule,
    PageSectionPortfolioModule,
    PortfolioHeader,
    FilterBadge,
    RouterOutlet,
    Link,
    Icon,
    TranslateModule
  ]
})
export class PortfolioLayout {
  protected readonly LabelGroupDTO = LabelGroupDTO;

  protected portfolioLayoutData = signal<PortfolioLayoutData | null>(null);

  protected readonly environment = inject(ENVIRONMENT);
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly userService = inject(UserService);
  protected readonly sharedService = inject(SharedService);
  protected readonly cachedPortfolioService = inject(CachedPortfolioService);
  protected readonly errorService = inject(ErrorService);
  protected readonly navService = inject(NavService);
  protected readonly modalService = inject(ModalService);
  protected readonly addBuildingService = inject(AddBuildingService);
  protected readonly portfoliosService = inject(PortfoliosService);
  protected readonly labelGroupService = inject(LabelGroupService);
  protected readonly measurementService = inject(MeasurementService);
  protected readonly buildingSearchFiltersService = inject(BuildingSearchFiltersService);
  protected readonly searchFiltersService = inject(SearchFiltersService);
  protected readonly buildingImportService = inject(BuildingImportService);
  protected readonly alertService = inject(AlertService);
  protected readonly frontendPortfolioImportService = inject(FrontendPortfolioImportService);
  protected readonly routeUtility = inject(RouteUtility);

  protected readonly hasClimateRiskLicense = toSignal(
    this.userService.hasLicense(LicenseDTO.LicenseTypeEnum.ClimateRisk)
  );

  protected readonly showNoBuildingsInPortfolio = toSignal(
    this.routeUtility.getDataFromChildRoutes('showNoBuildingsInPortfolio', true),
    { initialValue: true }
  );

  protected readonly portfolioId = toSignal(
    this.route.params.pipe(
      map((params) => {
        const id = params['portfolioId'];
        return id ? parseInt(id) : undefined;
      })
    )
  );

  protected readonly portfolio = toSignal(
    toObservable(this.portfolioId).pipe(
      mergeMap((portfolioId) => {
        if (!portfolioId) {
          return of(undefined);
        }
        return this.cachedPortfolioService.v2PortfoliosIdGet(portfolioId);
      }),
      catchError(() => {
        this.router.navigate(['energy']);
        return of(undefined);
      })
    )
  );

  protected readonly navbar = toSignal(
    toObservable(this.portfolio).pipe(
      mergeMap((portfolio) => {
        if (!portfolio) {
          return of(undefined);
        }
        return this.navService.getNavbar('energy', {
          portfolioId: portfolio.id,
          title: portfolio.name,
          demo: portfolio.demoPortfolio
        });
      })
    )
  );

  protected readonly tabbar = toSignal(
    toObservable(this.portfolio).pipe(
      mergeMap((portfolio) => {
        if (!portfolio) {
          return of(undefined);
        }
        return this.navService.getTabbar('portfolio', { portfolioId: portfolio.id, demo: portfolio.demoPortfolio });
      })
    )
  );

  private reloadTrigger = this.sharedService.reloadComponentObservable.pipe(map(() => new Date().getTime()));

  private portfolioBuildingLimit = toSignal(
    combineLatest([toObservable(this.portfolioId), this.reloadTrigger.pipe(startWith(null))]).pipe(
      switchMap(([portfolioId]) => {
        if (!portfolioId) {
          return of(null);
        }
        return this.cachedPortfolioService.v2PortfoliosIdBuildingLimitGet(portfolioId);
      }),
      catchError(() => of(null))
    )
  );

  private readonly hasBuildingsCreateScope = computed(() => this.userService.hasScope('buildings-create'));

  protected readonly portfolioImportStatus = toSignal(
    combineLatest([
      toObservable(this.portfolioId),
      toObservable(this.buildingImportService.hasFrontendPortfolioImportOrRelationManagementLicense),
      toObservable(this.buildingImportService.hasFrontendPortfolioImportReadScope)
    ]).pipe(
      switchMap(
        ([portfolioId, hasFrontendPortfolioImportOrRelationManagementLicense, hasFrontendPortfolioImportReadScope]) => {
          if (
            !portfolioId ||
            !hasFrontendPortfolioImportOrRelationManagementLicense ||
            !hasFrontendPortfolioImportReadScope
          ) {
            return of(undefined);
          }
          return this.frontendPortfolioImportService.v2PortfolioImportPortfolioIdStatusGet(portfolioId).pipe(
            map((portfolioImport: FrontendPortfolioImportDTO) => portfolioImport.status),
            catchError(() => of(undefined))
          );
        }
      ),
      catchError(() => of(undefined))
    )
  );

  private readonly hasImportLicenseAndScope = computed(() => {
    const hasFrontendPortfolioImportOrRelationManagementLicense =
      this.buildingImportService.hasFrontendPortfolioImportOrRelationManagementLicense();
    if (
      hasFrontendPortfolioImportOrRelationManagementLicense &&
      this.buildingImportService.hasFrontendPortfolioImportReadScope() &&
      this.portfolioImportStatus() !== FrontendPortfolioImportDTO.StatusEnum.COMPLETED &&
      this.portfolio()?.importUploadDisabled === false
    ) {
      return true;
    } else if (
      hasFrontendPortfolioImportOrRelationManagementLicense &&
      this.userService.hasRole('ROLE_RELATION_MANAGER')
    ) {
      return true;
    }

    return false;
  });

  protected readonly canAddMultipleBuildingsManually = computed(() => {
    if (
      this.portfolio()?.importUploadDisabled ||
      !this.buildingImportService.hasFrontendPortfolioImportOrRelationManagementLicense()
    ) {
      return false;
    }
    if (
      this.buildingImportService.hasFrontendPortfolioImportReadScope() ||
      this.userService.hasRole('ROLE_RELATION_MANAGER')
    ) {
      return true;
    }
    return false;
  });

  protected labelGroup = this.labelGroupService.currentLabelGroup;
  protected labelGroups = this.labelGroupService.possibleLabelGroups;
  protected showM2 = this.buildingSearchFiltersService.showM2;
  protected filterBadges = this.buildingSearchFiltersService.filterBadges;

  constructor() {
    window.scrollTo(0, 0);

    toObservable(this.portfolioId)
      .pipe(
        switchMap((portfolioId) => {
          if (!portfolioId) {
            return of(undefined);
          }
          this.buildingSearchFiltersService.isLoadingSearchFilters.set(true);
          return this.searchFiltersService.v2UserSearchFiltersBuildingPortfolioIdGet(portfolioId).pipe(
            tap((buildingSearchFilters: BuildingSearchFiltersDTO) => {
              this.buildingSearchFiltersService.defaultSearchFilters.set(buildingSearchFilters);
              this.buildingSearchFiltersService.searchFilters.set(buildingSearchFilters);
              if (buildingSearchFilters?.labelGroup) {
                this.labelGroupService.storePreferredLabelGroup(buildingSearchFilters.labelGroup);
              }
              this.showM2.set(buildingSearchFilters.amountsPerM2 === true);
              this.filterBadges.set(this.buildingSearchFiltersService.createBadges(buildingSearchFilters));
            }),
            switchMap(() => this.buildingSearchFiltersService.getFacetsBy(portfolioId)),
            tap((buildingSearchFacets: BuildingSearchFacetsDTO) => {
              if (
                buildingSearchFacets.labelGroups?.length === 0 &&
                this.hasBuildingsCreateScope() &&
                !this.customerBuildingLimitReached()
              ) {
                this.showNoBuildingsInPortfolioConfirmation();
              }
              this.labelGroupService.possibleLabelGroups.set(buildingSearchFacets.labelGroups);
              this.buildingSearchFiltersService.isLoadingSearchFilters.set(false);
            }),
            catchError(() => {
              this.buildingSearchFiltersService.isLoadingSearchFilters.set(false);
              return of(undefined);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    effect(() => {
      const portfolioId = this.portfolioId();
      const portfolio = this.portfolio();
      if (!portfolioId) {
        return;
      }
      this.portfolioLayoutData.set({
        portfolioId: portfolioId,
        portfolio: portfolio
      });
    });

    effect(() => {
      const labelGroup = this.labelGroup();
      if (!labelGroup) {
        return;
      }
      this.onLabelGroupChange(labelGroup);
    });
  }

  protected onTabClick(tab: { event: string }): void {
    if (tab.event === 'addBuilding') {
      this.addBuilding();
    }
  }

  protected isShowingFilters = this.buildingSearchFiltersService.isShowingFilters;

  protected async showFilters(): Promise<void> {
    const portfolioId = this.portfolioId();
    if (!portfolioId) {
      return;
    }
    return this.buildingSearchFiltersService.showFilters(portfolioId);
  }

  protected clearFilters(mouseEvent: MouseEvent): void {
    const portfolioId = this.portfolioId();
    if (!portfolioId) {
      return;
    }

    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.buildingSearchFiltersService.searchFilters.update(
      (searchFilters) => ({ labelGroup: searchFilters.labelGroup, amountsPerM2: searchFilters.amountsPerM2 }) as any
    );
    this.buildingSearchFiltersService.clearBadges();
    this.searchFiltersService
      .v2UserSearchFiltersBuildingPortfolioIdPost(this.buildingSearchFiltersService.searchFilters(), portfolioId)
      .subscribe();
  }

  protected toggleM2(): void {
    const portfolioId = this.portfolioId();
    if (!portfolioId) {
      return;
    }
    this.showM2.update((showM2) => !showM2);
    this.buildingSearchFiltersService.searchFilters.update((searchFilters) => ({
      ...searchFilters,
      amountsPerM2: this.showM2()
    }));
    this.searchFiltersService
      .v2UserSearchFiltersBuildingPortfolioIdPost(this.buildingSearchFiltersService.searchFilters(), portfolioId)
      .subscribe();
  }

  private readonly customerBuildingLimitReached = computed(() => {
    if (this.portfolioBuildingLimit() !== undefined) {
      return this.portfolioBuildingLimit()?.hasLimitReached === true;
    } else {
      return true;
    }
  });

  protected addBuilding(): void {
    const portfolioId = this.portfolioId();
    const portfolio = this.portfolio();
    if (!portfolioId || !portfolio) {
      return;
    }
    if (this.customerBuildingLimitReached()) {
      this.showPortfolioBuildingLimitReached();
    } else {
      this.addBuildingService.addBuilding(portfolioId, mapToPortfolioDTO(portfolio));
    }
  }

  protected readonly canAddMultipleBuildingsAutomated = toSignal(
    toObservable(this.portfolioId).pipe(
      switchMap((portfolioId) => {
        if (!portfolioId) {
          return of(undefined);
        }
        return this.buildingImportService.canAddMultipleBuildingsAutomated(portfolioId);
      })
    )
  );

  protected async showAddBuildingModal(showOnlyInternationalShortFlowForm: boolean = false): Promise<void> {
    const portfolioId = this.portfolioId();
    const portfolio = this.portfolio();
    if (!portfolioId || !portfolio) {
      return;
    }
    return this.addBuildingService.addBuilding(portfolioId, mapToPortfolioDTO(portfolio), {
      showOnlyInternationalShortFlowForm
    });
  }

  protected onLabelGroupChange(labelGroup: LabelGroupDTO): void {
    const portfolioId = this.portfolioId();
    if (!portfolioId) {
      return;
    }
    this.buildingSearchFiltersService.searchFilters.update((searchFilters: BuildingSearchFiltersDTO) => {
      const merged: BuildingSearchFiltersDTO = {
        ...searchFilters,
        labelGroup: labelGroup,
        amountsPerM2: searchFilters.amountsPerM2
      };
      // Same group as saved filters: do not wipe hasLabel / index facets (objectTypes was never cleared).
      if (searchFilters.labelGroup === labelGroup) {
        return merged;
      }
      return { ...this.clearSearchFilterFields(merged) };
    });
    this.filterBadges.set(
      this.buildingSearchFiltersService.createBadges(this.buildingSearchFiltersService.searchFilters())
    );
    this.searchFiltersService
      .v2UserSearchFiltersBuildingPortfolioIdPost(this.buildingSearchFiltersService.searchFilters(), portfolioId)
      .subscribe();
  }

  private clearSearchFilterFields(searchFilters: BuildingSearchFiltersDTO): BuildingSearchFiltersDTO {
    const fieldsToReset = [
      'improvedIndexLabels',
      'currentIndexLabels',
      'hasLabel',
      'crremStatuses',
      'indexLabelStatuses'
    ];

    fieldsToReset.forEach((field) => {
      if (field in searchFilters) {
        (searchFilters as any)[field] = null;
      }
    });

    return searchFilters;
  }

  private showPortfolioBuildingLimitReached() {
    this.alertService.showAlert('warning', 'addBuilding.warning.buildingsLimitReached');
  }
  protected removeFilterBadge(index: number): void {
    const portfolioId = this.portfolioId();
    if (!portfolioId) {
      return;
    }
    this.buildingSearchFiltersService.removeBadge(index);

    if (this.filterBadges().length < 1) {
      this.buildingSearchFiltersService.searchFilters.update(
        (searchFilters) => ({ labelGroup: searchFilters.labelGroup, amountsPerM2: searchFilters.amountsPerM2 }) as any
      );
    } else {
      this.buildingSearchFiltersService.searchFilters.update((searchFilters) =>
        this.buildingSearchFiltersService.reducePortfolioFilters(searchFilters)
      );
    }

    this.searchFiltersService
      .v2UserSearchFiltersBuildingPortfolioIdPost(this.buildingSearchFiltersService.searchFilters(), portfolioId)
      .subscribe();
  }

  private showNoBuildingsInPortfolioConfirmation(): void {
    const portfolio = this.portfolio();
    if (!portfolio) {
      return;
    }
    if (!this.showNoBuildingsInPortfolio()) {
      return;
    }

    if (this.modalService.getModalsCount() > 0) {
      return;
    }

    let alertConfirmation = {
      title: 'energy.noBuildingsInPortfolio.title',
      body: 'energy.noBuildingsInPortfolio.body',
      confirmText: 'addNewBuilding',
      cancelText: 'buttons.cancel',
      dataTest: 'noBuildingsInPortfolioModal'
    };

    if (this.hasImportLicenseAndScope() || this.buildingImportService.hasAutomatedPortfolioImportLicense()) {
      alertConfirmation = { ...alertConfirmation, ...{ secondaryConfirmText: 'addUploadMultipleBuildings' } };
    }

    this.alertService.showConfirmation(alertConfirmation).subscribe({
      next: (result: AlertModalResult) => {
        if (result === AlertModalResult.SECONDARY_CONFIRM) {
          this.buildingImportService.importBuildings(portfolio);
          return;
        }

        if (result === AlertModalResult.CONFIRM) {
          this.showAddBuildingModal();
        }
      }
    });
  }
}
